import { StageDisplayOrderUpdate } from './../models/stage-displayorder-update.model';
import { Injectable } from '@angular/core';
import { TitleCasePipe } from '@angular/common';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

// Models
import { Channel } from '../models/channel.model';
import { Stage } from '../models/stage.model';
import { Template } from '../models/template.model';
import { TemplateUpdate } from '../models/templateUpdate.model';

// Services
import { HttpService } from 'src/app/modules/shared/services/http.service';
import { MessageService } from 'primeng/api';

// Constants and Enums
import { Messages } from 'src/app/configurations/crm/constants/constants';

@Injectable({
    providedIn: 'root'
})

export class CrmSharedConfigurationService {
    baseConfigURL: string;

    constructor(
        private httpService: HttpService,
        private messageService: MessageService,
        private titleCasePipe: TitleCasePipe) {
        this.baseConfigURL = environment.configUrlV2;
    }

    // Channel Endpoints
    getChannels(moduleId: number): Observable<Channel[]> {
        return this.httpService.getAll(`${this.baseConfigURL}/Channel/Module/${moduleId}`);
    }

    createChannel(channelData: Partial<Channel>): Observable<Channel> {
        return this.httpService.create(`${this.baseConfigURL}/Channel`, channelData);
    }

    updateChannel(channelData: Channel): Observable<Channel> {
        return this.httpService.update(`${this.baseConfigURL}/Channel`, channelData);
    }

    setDefaultChannel(moduleId: number, channelId: number): Observable<Channel> {
        return this.httpService.update(`${this.baseConfigURL}/Channel/Default/Module/${moduleId}/Channel/${channelId}`, {});
    }

    deleteChannel(moduleId: number, channelId: number): Observable<Channel> {
        return this.httpService.delete(`${this.baseConfigURL}/Channel/Module/${moduleId}/Channel/${channelId}`);
    }

    // Stage Endpoints
    getChannelStages(moduleId: number, channelId: number): Observable<Stage[]> {
        return this.httpService.getAll(`${this.baseConfigURL}/Stage/Module/${moduleId}/Channel/${channelId}`);
    }

    createStage(stageData: Stage): Observable<Stage> {
        return this.httpService.create(`${this.baseConfigURL}/Stage`, stageData);
    }

    updateStage(stageData: Stage): Observable<Stage> {
        return this.httpService.update(`${this.baseConfigURL}/Stage`, stageData);
    }

    updateStageDisplayOrder(stageData: StageDisplayOrderUpdate): Observable<any> {
        return this.httpService.update(`${this.baseConfigURL}/Stage/DisplayOrder`, stageData);
    }

    deleteStage(moduleId: number, channelId: number, stageId: number): Observable<Stage> {
        return this.httpService.delete(`${this.baseConfigURL}/Stage/Module/${moduleId}/Channel/${channelId}/Stage/${stageId}`);
    }

    // Template Endpoints
    getTemplates(moduleId: number): Observable<Template[]> {
        return this.httpService.getAll(`${this.baseConfigURL}/Template/Module/${moduleId}`);
    }

    getTemplateDetails(moduleId: number, templateId: number): Observable<Template> {
        return this.httpService.getById(`${this.baseConfigURL}/Template/Module/${moduleId}/Template/${templateId}`);
    }

    updateTemplate(templateBody: TemplateUpdate): Observable<Template> {
        return this.httpService.update(`${this.baseConfigURL}/Template/SectionFields`, templateBody);
    }

    // Toast
    openToast(severity: string, detail: string): void {
        this.messageService.add({
            severity,
            summary: this.titleCasePipe.transform(severity),
            detail
        });
    }

    // Error Message
    getErrorMessage({ error }, actionType: string): string {
        const { StatusCode, Message } = error;
        let actionMessage: string = '';

        switch(actionType) {
            case 'CHANNEL_CREATE': {
                actionMessage = Messages.Channel_Created_Error;
                break;
            }
            case 'CHANNEL_UPDATE': {
                actionMessage = Messages.Channel_Updated_Error;
                break;
            }
            case 'SET_DEFAULT_CHANNEL': {
                actionMessage = Messages.Set_Default_Channel_Error;
                break;
            }
            case 'CHANNEL_DELETE': {
                actionMessage = Messages.Channel_Deleted_Error;
                break;
            }
            case 'STAGE_CREATE': {
                actionMessage = Messages.Stage_Created_Error;
                break;
            }
            case 'STAGE_UPDATE': {
                actionMessage = Messages.Stage_Updated_Error;
                break;
            }
            case 'STAGE_DELETE': {
                actionMessage = Messages.Stage_Deleted_Error;
                break;
            }
            case 'TEMPLATE_UPDATE': {
                actionMessage = Messages.Template_Updated_Error;
                break;
            }
        }

        const messageText = StatusCode === 500 ? actionMessage : Message;

        return messageText;
    }
}
